import { useProjectsData } from '../../../hooks/useProjectsData';
import { useCallback, useEffect } from 'react';
import { extractRootDomain } from '../../../utils/Utils';
import { useMediaQuery } from '@mui/material';

const ProjectsPerformanceController = (model) => {
  const { fetchProjectsPerformance, dataPerformance, loadingPerformance, fetchProjectInfo } = useProjectsData();

  useEffect(() => {
    const storedFavorites = localStorage.getItem('favoriteProjects');
    const storedShowFavorites = localStorage.getItem('showFavorites');
    const storedShowNonFavorites = localStorage.getItem('showNonFavorites');
    if (storedFavorites) {
      model.setFavoriteProjects(new Set(JSON.parse(storedFavorites)));
    }
    if (storedShowFavorites !== null) {
      model.setShowFavorites(JSON.parse(storedShowFavorites));
    }
    if (storedShowNonFavorites !== null) {
      model.setShowNonFavorites(JSON.parse(storedShowNonFavorites));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('favoriteProjects', JSON.stringify(Array.from(model.favoriteProjects)));
  }, [model.favoriteProjects]);

  useEffect(() => {
    localStorage.setItem('showFavorites', JSON.stringify(model.showFavorites));
    localStorage.setItem('showNonFavorites', JSON.stringify(model.showNonFavorites));
  }, [model.showFavorites, model.showNonFavorites]);


  const handleGlobalMetricToggle = (metric: string) => model.setGlobalMetrics(prev => ({
    ...prev,
    [metric]: !prev[metric]
  }));


  const toggleFavorite = useCallback((projectUrl) => {
    model.setFavoriteProjects(prev =>
      new Set(prev.has(projectUrl) ? [...prev].filter(url => url !== projectUrl) : [...prev, projectUrl])
    );
  }, []);

  const fetchAndSetPerformanceData = useCallback(async () => {
    if (!model.filter.get('dateFrom') || !model.filter.get('dateTo')) return;
    try {
      await fetchProjectsPerformance({ dateFrom: model.filter.get('dateFrom'), dateTo: model.filter.get('dateTo') });
    } catch (error) {
      console.error('Error fetching performance data:', error);
    }
  }, [fetchProjectsPerformance, model.filter]);

  const processPerformanceData = useCallback(() => {
    if (!dataPerformance) return;
    type AccumulatorType = {
      date: string;
      [key: string]: number | string;
    };
    const domainData = dataPerformance.items.flatMap(({ url, data }) =>
      data.map(({ keys, impressions }) => ({ url: extractRootDomain(url), date: keys[0], impressions }))
    ).reduce((acc: AccumulatorType[], { url, date, impressions }) => {
      const entry = acc.find((e) => e.date === date);
      entry ? (entry[url] = impressions) : acc.push({ date, [url]: impressions });
      return acc;
    }, []);
    model.setCustomDomainData(domainData);
  }, [dataPerformance]);

  useEffect(() => {
    fetchAndSetPerformanceData();
  }, [fetchAndSetPerformanceData]);

  useEffect(() => {
    if (!loadingPerformance) processPerformanceData();
  }, [loadingPerformance, processPerformanceData]);

  // Applu params when any of the filter changes
  const handleFilterUpdate = (name, from, to, action) => {
    model.setFilter((prevItems) => {
      const newItems = new Map([...prevItems]);

      // Initialize an object to store filter values directly
      const payload = {};

      if (action === "delete") {
        // Remove the filter keys from the Map
        newItems.delete(`${name}From`);
        newItems.delete(`${name}To`);
        newItems.delete(`${name}`);
      } else if (name === 'intention') {
        newItems.set(`${name}`, action);
      } else {
        if (from) newItems.set(`${name}From`, from);
        if (to) newItems.set(`${name}To`, to);
      }

      // Convert the Map into an object with only raw values
      newItems.forEach((value, key:any) => {
        payload[key] = value; // Store only the value, no extra object
      });

      // Update model state (if needed)
      model.setFilter(newItems);

      return new Map(newItems); // Ensure React detects changes
    });
  };


  return {
    handleGlobalMetricToggle,
    dataPerformance,
    toggleFavorite,
    loadingPerformance,
    handleFilterUpdate
  };


};

export default ProjectsPerformanceController;